import { Fragment, useMemo, useState } from "react";
import {
  Ship,
  ChevronDown,
  ChevronRight,
  Package,
  MapPin,
  Loader2,
  History as HistoryIcon,
  ExternalLink,
} from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@/lib/api";

// The five locked freight statuses. Everything the scan writes maps to one of
// these; mapStatus() also absorbs legacy/variant strings so old rows still render.
type FreightStatus = "pending" | "booked" | "on_the_water" | "canceled" | "delivered";

type Booking = {
  id: string;
  bookingNum: string;
  vendorInvoice: string;
  carrier: string;
  origin: string;
  destination: string;
  containerType: string;
  etd: string;
  eta: string;
  status: FreightStatus;
  vendor: string;
  sourceEmailLink: string;
};

type RawFreight = {
  id: number;
  booking: string | null;
  vendor_invoice: string | null;
  carrier: string | null;
  origin: string | null;
  destination: string | null;
  container: string | null;
  etd: string | null;
  eta: string | null;
  status: string | null;
  vendor: string | null;
  source_email_link: string | null;
};

type RawHistory = {
  id: number;
  vendor_invoice: string;
  changed_on: string | null;
  field: string;
  old_value: string | null;
  new_value: string | null;
  source_email_link: string | null;
};

const STATUS_BADGE: Record<FreightStatus, { label: string; className: string }> = {
  pending: { label: "Pending", className: "bg-slate-100 text-slate-600 border-slate-200" },
  booked: { label: "Booked", className: "bg-blue-50 text-blue-700 border-blue-200" },
  on_the_water: { label: "On the Water", className: "bg-cyan-50 text-cyan-700 border-cyan-200" },
  canceled: { label: "Canceled", className: "bg-red-50 text-red-700 border-red-200" },
  delivered: { label: "Delivered", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

function mapStatus(s: string | null): FreightStatus {
  switch ((s || "").toLowerCase().replace(/\s+/g, "_")) {
    case "booked":
    case "confirmed":
      return "booked";
    case "on_the_water":
    case "in_transit":
      return "on_the_water";
    case "delivered":
    case "arrived":
      return "delivered";
    case "canceled":
    case "cancelled":
      return "canceled";
    default:
      return "pending";
  }
}

function formatDate(s: string | null): string {
  if (!s) return "TBD";
  try {
    return new Date(s).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "TBD";
  }
}

const FIELD_LABEL: Record<string, string> = {
  booking: "Booking #",
  status: "Status",
  etd: "ETD",
  eta: "ETA",
  carrier: "Carrier",
  container: "Container",
  origin: "Origin",
  destination: "Destination",
};

const TABLE_COLSPAN = 11;

export function FreightPage() {
  const api = useApi();
  const [vendorFilter, setVendorFilter] = useState("All");
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ["freight"],
    queryFn: async (): Promise<RawFreight[]> => {
      const res = await api(`/api/freight`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as RawFreight[];
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  const { data: historyData } = useQuery({
    queryKey: ["freight-history"],
    queryFn: async (): Promise<RawHistory[]> => {
      const res = await api(`/api/freight/history`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as RawHistory[];
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  });

  // Group history entries by the stable vendor_invoice key.
  const historyByInvoice = useMemo(() => {
    const map = new Map<string, RawHistory[]>();
    for (const h of historyData ?? []) {
      if (!h.vendor_invoice) continue;
      const list = map.get(h.vendor_invoice) ?? [];
      list.push(h);
      map.set(h.vendor_invoice, list);
    }
    return map;
  }, [historyData]);

  const bookings: Booking[] = useMemo(
    () =>
      (data ?? []).map((row) => ({
        id: String(row.id),
        bookingNum: row.booking || "",
        vendorInvoice: row.vendor_invoice || "",
        carrier: row.carrier || "",
        origin: row.origin || "",
        destination: row.destination || "",
        containerType: row.container || "—",
        etd: formatDate(row.etd),
        eta: formatDate(row.eta),
        status: mapStatus(row.status),
        vendor: row.vendor || "",
        sourceEmailLink: row.source_email_link || "",
      })),
    [data],
  );

  const allVendors = ["All", ...Array.from(new Set(bookings.map((b) => b.vendor).filter(Boolean)))];

  const filteredBookings =
    vendorFilter === "All" ? bookings : bookings.filter((b) => b.vendor === vendorFilter);

  function toggleRow(id: string) {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div
      className="flex flex-col h-full"
      style={{ fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif" }}
    >
      {/* Header */}
      <div className="px-8 py-5 border-b bg-background flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-cyan-50 flex items-center justify-center">
            <Ship className="w-5 h-5 text-cyan-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Freight Tracker</h1>
            <p className="text-xs text-muted-foreground">Active bookings and shipping status</p>
          </div>
        </div>

        {/* Vendor filter */}
        <div className="flex items-center gap-2 flex-wrap">
          {allVendors.map((v) => (
            <button
              key={v}
              onClick={() => setVendorFilter(v)}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg border transition-colors ${
                vendorFilter === v
                  ? "bg-foreground text-background border-foreground"
                  : "bg-background text-muted-foreground border-border hover:bg-muted/50"
              }`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-[#f8fafc] p-6 space-y-6">
        {/* Active Bookings Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b flex items-center gap-2">
            <Package className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-semibold text-foreground">Active Bookings</span>
            <span className="ml-auto text-xs text-muted-foreground flex items-center gap-1">
              {isLoading && <Loader2 className="w-3 h-3 animate-spin" />}
              {filteredBookings.length} bookings
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/30 border-b">
                  <th className="w-8 px-2 py-2.5" />
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Booking #</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Vendor Invoice</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Carrier</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Origin</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Destination</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Container</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">ETD</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">ETA</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Status</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted-foreground">Vendor</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredBookings.map((b) => {
                  const badge = STATUS_BADGE[b.status];
                  const isExpanded = expandedRows.has(b.id);
                  const history = b.vendorInvoice ? historyByInvoice.get(b.vendorInvoice) ?? [] : [];
                  return (
                    <Fragment key={b.id}>
                      <tr
                        onClick={() => toggleRow(b.id)}
                        className="hover:bg-muted/20 transition-colors cursor-pointer"
                      >
                        <td className="px-2 py-3 text-muted-foreground">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono font-medium text-foreground">
                          {b.sourceEmailLink ? (
                            <a
                              href={b.sourceEmailLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-cyan-700 hover:underline"
                            >
                              {b.bookingNum || "—"}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : (
                            b.bookingNum || "—"
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-muted-foreground">{b.vendorInvoice || "—"}</td>
                        <td className="px-4 py-3 text-xs text-foreground">{b.carrier}</td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            {b.origin}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            {b.destination}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-foreground">{b.containerType}</td>
                        <td className="px-4 py-3 text-xs text-blue-600 font-medium">{b.etd}</td>
                        <td className="px-4 py-3 text-xs text-emerald-600 font-medium">{b.eta}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${badge.className}`}>
                            {badge.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-foreground">{b.vendor}</td>
                      </tr>
                      {isExpanded && (
                        <tr className="bg-muted/10">
                          <td />
                          <td colSpan={TABLE_COLSPAN - 1} className="px-4 py-4">
                            <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-foreground">
                              <HistoryIcon className="w-3.5 h-3.5 text-muted-foreground" />
                              History
                              {b.vendorInvoice && (
                                <span className="font-mono font-normal text-muted-foreground">· {b.vendorInvoice}</span>
                              )}
                            </div>
                            {history.length === 0 ? (
                              <p className="text-xs text-muted-foreground">No changes recorded yet.</p>
                            ) : (
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="text-muted-foreground">
                                    <th className="text-left font-medium py-1 pr-4">Date</th>
                                    <th className="text-left font-medium py-1 pr-4">Field</th>
                                    <th className="text-left font-medium py-1 pr-4">From</th>
                                    <th className="text-left font-medium py-1 pr-4">To</th>
                                    <th className="text-left font-medium py-1">Source</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {history.map((h) => (
                                    <tr key={h.id} className="border-t border-border/50">
                                      <td className="py-1.5 pr-4 text-muted-foreground whitespace-nowrap">{formatDate(h.changed_on)}</td>
                                      <td className="py-1.5 pr-4 font-medium text-foreground">{FIELD_LABEL[h.field] || h.field}</td>
                                      <td className="py-1.5 pr-4 text-muted-foreground">{h.old_value || "—"}</td>
                                      <td className="py-1.5 pr-4 text-foreground font-medium">{h.new_value || "—"}</td>
                                      <td className="py-1.5">
                                        {h.source_email_link ? (
                                          <a
                                            href={h.source_email_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-1 text-cyan-700 hover:underline"
                                          >
                                            email
                                            <ExternalLink className="w-3 h-3" />
                                          </a>
                                        ) : (
                                          "—"
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
                {filteredBookings.length === 0 && !isLoading && (
                  <tr>
                    <td colSpan={TABLE_COLSPAN} className="px-4 py-10 text-center text-xs text-muted-foreground">
                      No bookings yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

(FreightPage as unknown as { path: string }).path = "/freight";
